import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Send, X, Megaphone } from "lucide-react";
import { imageUrl } from "@/components/ImageUpload";

const isImage = (ct) => (ct || "").startsWith("image/");

const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const fileSize = (n) => {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export const AttachmentList = ({ attachments }) => {
    if (!attachments || attachments.length === 0) return null;
    return (
        <div className="mt-2 flex flex-wrap gap-2">
            {attachments.map((a, i) => (
                <a key={i} href={`${API}/files/${a.path}`} target="_blank" rel="noreferrer"
                   className="block rounded-lg border border-brand-line bg-white max-w-[200px] overflow-hidden hover:border-brand-forest transition">
                    {isImage(a.content_type) ? (
                        <img src={`${API}/files/${a.path}`} alt={a.name} className="w-full h-32 object-cover" />
                    ) : (
                        <div className="p-3 flex items-center gap-2">
                            <div className="h-9 w-9 rounded bg-brand-mint flex items-center justify-center text-brand-forest"><Paperclip size={16} /></div>
                            <div className="min-w-0">
                                <div className="text-xs font-semibold truncate">{a.name}</div>
                                <div className="text-[10px] text-brand-mute">{fileSize(a.size)}</div>
                            </div>
                        </div>
                    )}
                </a>
            ))}
        </div>
    );
};

/**
 * MessageComposer — text + attachments.
 *  - onSend({body, attachments}) — async, must return when complete
 */
export const MessageComposer = ({ onSend, placeholder = "Type a message…", testIdPrefix = "msg" }) => {
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const fileRef = useRef(null);

    const addFiles = async (e) => {
        const list = Array.from(e.target.files || []);
        if (list.length === 0) return;
        setUploading(true);
        try {
            const next = [...attachments];
            for (const f of list) {
                if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10MB`); continue; }
                const fd = new FormData();
                fd.append("file", f);
                const res = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
                next.push(res.data);
            }
            setAttachments(next);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeAt = (i) => setAttachments(attachments.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!body.trim() && attachments.length === 0) return;
        setSending(true);
        try {
            await onSend({ body: body.trim(), attachments });
            setBody(""); setAttachments([]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally { setSending(false); }
    };

    return (
        <form onSubmit={submit} className="rounded-xl border border-brand-line bg-white p-3" data-testid={`${testIdPrefix}-composer`}>
            {attachments.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {attachments.map((a, i) => (
                        <div key={i} className="inline-flex items-center gap-2 bg-brand-bone rounded-lg pl-2 pr-1 py-1 text-xs">
                            {isImage(a.content_type) ? <img src={`${API}/files/${a.path}`} alt="" className="h-6 w-6 rounded object-cover" /> : <Paperclip size={12} />}
                            <span className="font-semibold truncate max-w-[140px]">{a.name}</span>
                            <span className="text-brand-mute">{fileSize(a.size)}</span>
                            <button type="button" onClick={() => removeAt(i)} className="h-5 w-5 rounded-full hover:bg-white text-brand-mute" data-testid={`${testIdPrefix}-rm-${i}`}><X size={12} /></button>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-end gap-2">
                <textarea
                    data-testid={`${testIdPrefix}-input`}
                    className="flex-1 resize-none rounded-lg border-0 bg-transparent text-sm focus:outline-none px-2 py-2 min-h-[40px] max-h-32"
                    placeholder={placeholder}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }}
                    rows={2}
                />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    data-testid={`${testIdPrefix}-attach`}
                    className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-brand-line hover:bg-brand-bone text-brand-ink disabled:opacity-50">
                    <Paperclip size={16} />
                </button>
                <button type="submit" disabled={sending || uploading || (!body.trim() && attachments.length === 0)}
                    data-testid={`${testIdPrefix}-send`}
                    className="pill btn-coral h-10 px-4 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1">
                    <Send size={14} /> {sending ? "Sending…" : "Send"}
                </button>
            </div>
            <input ref={fileRef} type="file" onChange={addFiles} multiple className="hidden" data-testid={`${testIdPrefix}-file`} />
            <p className="text-[11px] text-brand-mute mt-1.5 px-2">⌘/Ctrl+Enter to send. Attach files up to 10MB each.</p>
        </form>
    );
};

/**
 * MessageThread — list of messages + composer
 * Props: conversationId, viewerRole ("host_company" | "vendor"), onChange()
 */
export const MessageThread = ({ conversationId, viewerRole, onChange }) => {
    const [conv, setConv] = useState(null);
    const bottomRef = useRef(null);

    const load = async () => {
        const res = await api.get(`/conversations/${conversationId}`);
        setConv(res.data);
        await api.post(`/conversations/${conversationId}/read`);
        onChange?.();
    };

    useEffect(() => {
        load();
        const interval = setInterval(load, 8000);
        return () => clearInterval(interval);
        /* eslint-disable-next-line */
    }, [conversationId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [conv?.messages?.length]);

    const send = async ({ body, attachments }) => {
        await api.post(`/conversations/${conversationId}/messages`, { body, attachments });
        await load();
    };

    if (!conv) return <div className="p-6 text-brand-mute">Loading conversation…</div>;

    return (
        <div className="flex flex-col h-full">
            <div className="px-5 py-4 border-b border-brand-line flex items-center gap-3 bg-white" data-testid="thread-header">
                <div className="h-10 w-10 rounded-xl bg-brand-mint flex items-center justify-center overflow-hidden font-heading font-extrabold text-brand-forest">
                    {conv.counterparty?.logo_url ? <img src={imageUrl(conv.counterparty.logo_url)} alt="" className="w-full h-full object-cover" /> : (conv.counterparty?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold truncate">{conv.counterparty?.name}</div>
                    <div className="text-xs text-brand-mute truncate">{conv.event?.name}</div>
                </div>
            </div>
            <div className="flex-1 overflow-auto px-5 py-4 space-y-4 bg-brand-bone/40" data-testid="thread-messages">
                {conv.messages.length === 0 && <p className="text-sm text-brand-mute text-center py-8">No messages yet. Say hi 👋</p>}
                {conv.messages.map((m) => {
                    const mine = m.sender_role === viewerRole;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-brand-forest text-white" : "bg-white border border-brand-line text-brand-ink"}`}>
                                {m.is_broadcast && (
                                    <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1 ${mine ? "text-brand-coral" : "text-brand-coral"}`}>
                                        <Megaphone size={10} /> Broadcast
                                    </div>
                                )}
                                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                                <AttachmentList attachments={m.attachments} />
                                <div className={`text-[10px] mt-1.5 ${mine ? "text-white/60" : "text-brand-mute"}`}>{fmtTime(m.created_at)}</div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>
            <div className="border-t border-brand-line p-3 bg-white">
                <MessageComposer onSend={send} testIdPrefix="thread" />
            </div>
        </div>
    );
};
